import React, { useState } from "react";
import { FormattedMessage as FM, useIntl } from "react-intl";
import { Button, Confirm, Form, Grid, Header, Icon, Message, Popup } from "semantic-ui-react";

import IParishGeneralData from "../../data/parish/ParishGeneralData";
import BoldFM from "../../util/styledMessages/BoldFM";
import ProfilePlaceholder from "../ProfilePlaceholder";
import SuggestionModal from "../util/suggestions/SuggestionModal";

interface IParishProfileHeaderProps {
    parish: IParishGeneralData;
    editMode: boolean;
    isAdmin: boolean;
    handleEditModeChange: (e: any, { value }: any) => void;
    setParish(newParish: IParishGeneralData): void;
    asyncDelete(url: string): void;
}

const ParishProfileHeader: React.FC<IParishProfileHeaderProps> = (props: IParishProfileHeaderProps) => {
    const { parish, editMode, isAdmin, handleEditModeChange, setParish, asyncDelete } = props;
    const { formatMessage: fm } = useIntl();

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const handleDeleteSubmit = () => {
        setDeleteConfirmOpen(false);
        asyncDelete("/parish/" + parish.ID);
    };

    const handleDeleteConfirmOpen = (_: any) => {
        setDeleteConfirmOpen(true);
    };

    const hasAllocations = (): boolean => {
        const nrMainAllocs = parish.mainPriests && parish.mainPriests.length ? parish.mainPriests.length : 0;
        const nrAssistAllocs = parish.assistantPriests && parish.assistantPriests.length ? parish.assistantPriests.length : 0;
        return nrMainAllocs + nrAssistAllocs > 0;
    };

    const toggleVerified = () => setParish({ ...parish, verified: !parish.verified });

    const [suggestionMessage, setSuggestionMessage] = useState<string>();

    if (!parish) {
        return <ProfilePlaceholder />;
    } else {
        return (
            <>
                <Confirm
                    cancelButton={fm({ id: "general.cancel" })}
                    confirmButton={fm({ id: "confirm.delete.confirm" })}
                    content={fm({ id: "confirm.delete.message.parish" })}
                    onCancel={() => setDeleteConfirmOpen(false)}
                    onConfirm={handleDeleteSubmit}
                    open={deleteConfirmOpen}
                />

                <Grid columns={12} stackable>
                    <Grid.Row textAlign="center" verticalAlign="middle">
                        <Grid.Column />
                        <Grid.Column width={editMode ? 10 : 7}>
                            <Grid.Row>
                                <Header as="h1">
                                    {parish.name}
                                    <span style={{ float: "right" }}>
                                        {editMode && (
                                            <Form.Checkbox
                                                checked={parish.verified}
                                                label={fm({ id: "profile.verified.label" })}
                                                name="verified"
                                                onChange={toggleVerified}
                                                toggle
                                            />
                                        )}
                                        {!editMode &&
                                            (parish.verified ? (
                                                <Popup
                                                    content={fm({ id: "profile.verified.isVerifiedPopup" })}
                                                    trigger={<Icon color="green" fitted name="check circle outline" />}
                                                />
                                            ) : (
                                                <Popup
                                                    content={fm({ id: "profile.verified.notVerifiedPopup" })}
                                                    trigger={<Icon color="grey" fitted name="question circle outline" />}
                                                />
                                            ))}
                                    </span>
                                </Header>
                            </Grid.Row>
                            {parish.cityName && (
                                <Grid.Row>
                                    <Header as="h3" textAlign="center">
                                        {parish.cityName}
                                    </Header>
                                </Grid.Row>
                            )}
                        </Grid.Column>
                        <Grid.Column />
                        <Grid.Column width={3}>
                            {!editMode && isAdmin && (
                                <>
                                    <Button
                                        color="violet"
                                        icon
                                        id="editModeButton"
                                        inverted
                                        labelPosition="left"
                                        onClick={handleEditModeChange}
                                        size="large"
                                        style={{ marginBottom: "1em", fontFamily: "monospace" }}
                                        value
                                    >
                                        <Icon name="edit" />
                                        <FM id="profile.edit" />
                                    </Button>
                                    <br />
                                    <Popup
                                        content={fm({ id: "error.parishNotRemovable" })}
                                        disabled={!hasAllocations()}
                                        key="deleteNoAlloc"
                                        trigger={
                                            <span>
                                                <Button
                                                    color="red"
                                                    disabled={hasAllocations()}
                                                    icon
                                                    id="removeButton"
                                                    inverted
                                                    labelPosition="left"
                                                    onClick={handleDeleteConfirmOpen}
                                                    size="large"
                                                    style={{ fontFamily: "monospace" }}
                                                    value
                                                >
                                                    <Icon name="trash" />
                                                    <FM id="profile.delete" />
                                                </Button>
                                            </span>
                                        }
                                    />
                                </>
                            )}
                            <Grid.Column width={3}>
                                {!editMode && !isAdmin && (
                                    <SuggestionModal setSuggestionMessage={setSuggestionMessage} targetId={parish.ID} targetType="parish" />
                                )}
                            </Grid.Column>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
                {suggestionMessage !== undefined && (
                    <Message
                        negative={suggestionMessage === "error.request" || suggestionMessage === "error.server"}
                        positive={suggestionMessage === "suggestion.sent"}
                    >
                        <BoldFM id={suggestionMessage} />
                    </Message>
                )}
            </>
        );
    }
};

export default ParishProfileHeader;
